import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BIO_KEY = "ep_biorhythm_dob";

/**
 * Landing-page mini form that takes a DOB and recommends a meditation from
 * the library based on today's biorhythm dip and current moon phase.
 *
 * On submit:
 *   1. Save DOB to localStorage (so your guidance banner + biorhythm panel
 *      also come pre-populated on the next visit).
 *   2. Call /api/meditations/recommend to pick the right track.
 *   3. Deep-link to /almanac?meditation=<slug> which auto-scrolls to and
 *      pre-selects the track in the meditation library.
 */
export default function MeditationRecommender() {
  const nav = useNavigate();
  const [dob, setDob] = useState(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem(BIO_KEY) || "";
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!dob) return;
    setLoading(true);
    try {
      try { localStorage.setItem(BIO_KEY, dob); } catch { /* ignore quota */ }
      const r = await fetch(`${BACKEND}/api/meditations/recommend?dob=${encodeURIComponent(dob)}`);
      const d = await r.json();
      setResult(d);
    } catch {
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const goToTrack = () => {
    if (!result?.recommended_slug) return;
    nav(`/almanac?meditation=${result.recommended_slug}`);
  };

  return (
    <div
      data-testid="meditation-recommender"
      className="mt-10 md:mt-12 rounded-2xl border border-[color:var(--gold)]/25 p-6 md:p-8 max-w-xl"
      style={{ background: "linear-gradient(135deg, rgba(212,162,76,0.06), rgba(229,168,124,0.02))" }}
    >
      <div className="flex items-center gap-2">
        <Sparkles size={14} className="text-[color:var(--gold)]" />
        <span className="label-overline text-[color:var(--gold)]/85">A gentle first step</span>
      </div>
      <h3 className="mt-3 font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl tracking-tight leading-tight">
        Tell us your birthday. We'll <span className="font-serif-italic text-[color:var(--gold)]">recommend</span> a meditation for today.
      </h3>
      <p className="mt-3 text-[color:var(--parchment-soft)] font-sans-body text-sm md:text-base leading-relaxed">
        We read today's biorhythm dip and current moon phase and pick one of five grounding tones — kept on this device only, no signup.
      </p>

      <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row items-stretch gap-3">
        <Input
          type="date"
          value={dob}
          onChange={(e) => setDob(e.target.value)}
          required
          className="bg-white border-slate-300 rounded-lg h-11 text-slate-900 font-sans-body flex-1"
          data-testid="rec-dob-input"
        />
        <Button
          type="submit"
          disabled={!dob || loading}
          data-testid="rec-submit"
          className="h-11 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium disabled:opacity-50"
        >
          {loading ? "reading…" : "Recommend my meditation"}
        </Button>
      </form>

      {result?.track && (
        <div
          data-testid="rec-result"
          className="mt-5 pt-5 border-t border-[color:var(--gold)]/20"
        >
          <div className="label-overline text-[color:var(--gold)]">For you today</div>
          <div className="mt-2 font-serif-display text-[color:var(--parchment)] text-xl md:text-2xl leading-tight">
            {result.track.title}
            <span className="font-serif-italic text-[color:var(--text-secondary)] text-sm md:text-base ml-2">
              · {result.track.duration_minutes} min
            </span>
          </div>
          <div className="mt-2 font-serif-italic text-[color:var(--text-secondary)] text-sm leading-relaxed">
            {result.why}
          </div>
          <button
            type="button"
            onClick={goToTrack}
            data-testid="rec-open-track"
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium text-sm"
          >
            Open in Guidance <ArrowRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
